import moment from 'moment-timezone'

type TimeZoneFormats = 'short' | 'long'

export const formatTimeZone = (timeZone: string, format: TimeZoneFormats = 'long') => {
  const fullTimeZoneName =
    Intl.DateTimeFormat('en-US', { timeZoneName: format, timeZone })
      .formatToParts(Date.now())
      .find((part) => part.type === 'timeZoneName')?.value ?? ''

  return fullTimeZoneName
}

export const getTimeZoneCityName = (timezone: string) => {
  const parts = timezone.split('/')
  if (!parts.length) return ''
  return parts[parts.length - 1].replace('_', ' ')
}

export const getTimeZoneDisplayRepresentation = (timezone: string | undefined) => {
  if (!timezone) timezone = moment.tz.guess()
  const formattedTimeZone = formatTimeZone(timezone)
  const formattedOffset = 'GMT' + moment.tz(timezone).format('Z')
  const cityName = getTimeZoneCityName(timezone)
  const formattedCityName = cityName && cityName.includes('GMT') ? '' : `- ${cityName}`
  const label = `(${formattedOffset}) ${
    formattedTimeZone.includes('GMT') ? '' : formattedTimeZone
  } ${formattedCityName}`
  return label
}

/**
 * Total UTC offset in MINUTES, read out of a display label such as
 * "(GMT+05:30) India Standard Time - Kolkata" or a bare "+05:30".
 *
 * Minutes, not hours: half- and quarter-hour zones are real (Kolkata +05:30,
 * Kathmandu +05:45, Chatham +12:45). Parsing only the hour made +05:30 and +05:00
 * compare equal, so a 30-minute drift went unnoticed.
 *
 * The match is anchored on the optional GMT prefix so a local time appended to the
 * label (ChangeTimezone does this) can never be picked up as the offset instead.
 */
export const getTimeZoneOffsetMinutes = (timeZoneOffsetString: string) => {
  const match = timeZoneOffsetString.match(/(?:GMT)?([+-])(\d{1,2}):?(\d{2})?/)
  if (!match) return 0
  const [, sign, hours, minutes] = match
  const total = parseInt(hours) * 60 + (minutes ? parseInt(minutes) : 0)
  return sign === '-' ? -total : total
}

export const isTimeZoneNameValid = (timezone: string) => {
  return !getTimeZoneCityName(timezone).includes('GMT') && !formatTimeZone(timezone).includes('GMT')
}

export const checkTimeZone = (
  user: { timezone: string | undefined; interimTimezone: string | undefined },
  currentTimezone: string
): boolean => {
  if (!user.timezone) return false
  const userTimeZoneOffset = getTimeZoneOffsetMinutes(
    getTimeZoneDisplayRepresentation(user.timezone)
  )
  const currentTimeZoneOffset = getTimeZoneOffsetMinutes(
    getTimeZoneDisplayRepresentation(currentTimezone)
  )
  const isTimeZoneInADifferentOffset =
    user?.timezone && userTimeZoneOffset !== currentTimeZoneOffset
  const userIsNotInTheInterimTimeZone =
    !user?.interimTimezone || user?.interimTimezone !== currentTimezone
  return !!(isTimeZoneInADifferentOffset && userIsNotInTheInterimTimeZone)
}
